import type {
  AdminFeedbackItem,
  AdminDashboardMetrics,
  AdminDashboardMetricsV2,
  AdminUserItem,
  AdminFeedbackNote,
  AdminActivityLogEntry,
  AdminUserDetail,
  UserActivitySummary,
  FeedbackStatus,
} from './types';

// ============================================================
// Feedback (extended with status/priority)
// ============================================================

export const mockAdminFeedback: AdminFeedbackItem[] = [
  {
    id: 'fb-1',
    user_id: 'u-1',
    user_email: 'sarah@techstart.io',
    platform: 'ios',
    category: 'bug',
    text: 'Score card sometimes shows NaN when I first load the dashboard. Closing and reopening the app fixes it.',
    screenshot_path: null,
    screen_context: '/(startup)/score',
    app_version: '1.0.0',
    device_info: { os: 'iOS 18.2', model: 'iPhone 15 Pro' },
    created_at: '2026-02-10T14:23:00Z',
    status: 'in_review',
    priority: 'high',
    assigned_to: 'u-10',
    resolved_at: null,
    updated_at: '2026-02-10T15:00:00Z',
  },
  {
    id: 'fb-2',
    user_id: 'u-2',
    user_email: 'marcus@alpine.vc',
    platform: 'ios',
    category: 'feature',
    text: 'Would love to export dealflow pipeline as a CSV or PDF for our weekly partner meetings.',
    screenshot_path: null,
    screen_context: '/(investor)/dealflow',
    app_version: '1.0.0',
    device_info: { os: 'iOS 18.1', model: 'iPhone 16' },
    created_at: '2026-02-09T09:45:00Z',
    status: 'new',
    priority: 'medium',
    assigned_to: null,
    resolved_at: null,
    updated_at: '2026-02-09T09:45:00Z',
  },
  {
    id: 'fb-3',
    user_id: 'u-3',
    user_email: 'lena@greenfield.co',
    platform: 'android',
    category: 'ux',
    text: 'The onboarding questionnaire feels too long. Maybe split it into smaller steps or allow skipping more sections.',
    screenshot_path: null,
    screen_context: '/(onboarding)/startup-personal',
    app_version: '1.0.0',
    device_info: { os: 'Android 15', model: 'Pixel 9' },
    created_at: '2026-02-08T16:12:00Z',
    status: 'new',
    priority: 'medium',
    assigned_to: null,
    resolved_at: null,
    updated_at: '2026-02-08T16:12:00Z',
  },
  {
    id: 'fb-4',
    user_id: 'u-4',
    user_email: 'jonas@finova.ch',
    platform: 'web',
    category: 'bug',
    text: 'LinkedIn verification button does nothing on web. Console shows CORS error.',
    screenshot_path: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=400',
    screen_context: '/(investor)/investor-dashboard',
    app_version: '1.0.0',
    device_info: { os: 'macOS', userAgent: 'Chrome 121' },
    created_at: '2026-02-07T11:30:00Z',
    status: 'resolved',
    priority: 'critical',
    assigned_to: 'u-10',
    resolved_at: '2026-02-09T16:00:00Z',
    updated_at: '2026-02-09T16:00:00Z',
  },
  {
    id: 'fb-5',
    user_id: 'u-1',
    user_email: 'sarah@techstart.io',
    platform: 'ios',
    category: 'feature',
    text: 'It would be great to get push notifications when an investor views my data room.',
    screenshot_path: null,
    screen_context: '/(startup)/dataroom',
    app_version: '1.0.0',
    device_info: { os: 'iOS 18.2', model: 'iPhone 15 Pro' },
    created_at: '2026-02-06T08:55:00Z',
    status: 'new',
    priority: 'low',
    assigned_to: null,
    resolved_at: null,
    updated_at: '2026-02-06T08:55:00Z',
  },
  {
    id: 'fb-6',
    user_id: 'u-5',
    user_email: 'anna@swissbio.ch',
    platform: 'ios',
    category: 'other',
    text: 'Love the app overall! The AI scoring feels really accurate for our biotech profile.',
    screenshot_path: null,
    screen_context: '/(startup)/startup-dashboard',
    app_version: '1.0.0',
    device_info: { os: 'iOS 18.2', model: 'iPhone 14' },
    created_at: '2026-02-05T15:20:00Z',
    status: 'closed',
    priority: 'low',
    assigned_to: null,
    resolved_at: null,
    updated_at: '2026-02-05T17:00:00Z',
  },
];

// ============================================================
// Users
// ============================================================

export const mockAdminUsers: AdminUserItem[] = [
  { id: 'u-1', email: 'sarah@techstart.io', role: 'startup', onboarding_completed: true, created_at: '2026-01-15T10:00:00Z' },
  { id: 'u-2', email: 'marcus@alpine.vc', role: 'investor', onboarding_completed: true, created_at: '2026-01-16T12:00:00Z' },
  { id: 'u-3', email: 'lena@greenfield.co', role: 'startup', onboarding_completed: true, created_at: '2026-01-20T09:00:00Z' },
  { id: 'u-4', email: 'jonas@finova.ch', role: 'investor', onboarding_completed: true, created_at: '2026-01-22T14:00:00Z' },
  { id: 'u-5', email: 'anna@swissbio.ch', role: 'startup', onboarding_completed: true, created_at: '2026-01-25T11:00:00Z' },
  { id: 'u-6', email: 'david@capitalbridge.com', role: 'investor', onboarding_completed: false, created_at: '2026-02-01T08:00:00Z' },
  { id: 'u-7', email: 'mia@cloudkitchen.io', role: 'startup', onboarding_completed: true, created_at: '2026-02-03T16:00:00Z' },
  { id: 'u-8', email: 'felix@zurich-angels.ch', role: 'investor', onboarding_completed: true, created_at: '2026-02-05T10:30:00Z' },
  { id: 'u-9', email: 'claire@medtech-lab.com', role: 'startup', onboarding_completed: false, created_at: '2026-02-08T13:00:00Z' },
  { id: 'u-10', email: 'admin@inverio.ai', role: 'admin', onboarding_completed: true, created_at: '2026-01-10T08:00:00Z' },
];

// ============================================================
// Legacy dashboard metrics (backward compat)
// ============================================================

export const mockAdminMetrics: AdminDashboardMetrics = {
  totalUsers: 10,
  totalStartups: 5,
  totalInvestors: 4,
  totalAdmins: 1,
  totalFeedback: 6,
  feedbackByCategory: {
    bug: 2,
    feature: 2,
    ux: 1,
    other: 1,
  },
  recentSignups7d: 3,
};

// ============================================================
// Dashboard metrics V2 (extended)
// ============================================================

export const mockAdminMetricsV2: AdminDashboardMetricsV2 = {
  totalUsers: 10,
  totalStartups: 5,
  totalInvestors: 4,
  totalAdmins: 1,
  totalFeedback: 6,
  feedbackByCategory: { bug: 2, feature: 2, ux: 1, other: 1 },
  recentSignups7d: 3,
  // Growth deltas (vs prior 7 days)
  usersDelta: 15,
  startupsDelta: 20,
  investorsDelta: 10,
  feedbackDelta: -5,
  // Engagement
  dau: 7,
  wau: 9,
  mau: 10,
  // Feedback management
  openFeedbackCount: 4,
  criticalBugs: 1,
  feedbackByStatus: { new: 3, in_review: 1, resolved: 1, closed: 1 },
  // Scores & activity
  avgScore: 68,
  messageVolume: 34,
  dataRoomActivity: 12,
  pendingOnboarding: 3,
  // Signup trend (last 7 days)
  signupTrend: [
    { date: '02/05', count: 1 },
    { date: '02/06', count: 0 },
    { date: '02/07', count: 0 },
    { date: '02/08', count: 2 },
    { date: '02/09', count: 0 },
    { date: '02/10', count: 1 },
    { date: '02/11', count: 0 },
  ],
};

// ============================================================
// Admin activity log
// ============================================================

export const mockAdminActivityLog: AdminActivityLogEntry[] = [
  {
    id: 'al-1',
    admin_email: 'admin@inverio.ai',
    action: 'feedback_status_changed',
    target_type: 'feedback',
    target_id: 'fb-1',
    details: { from: 'new', to: 'in_review' },
    created_at: '2026-02-10T15:00:00Z',
  },
  {
    id: 'al-2',
    admin_email: 'admin@inverio.ai',
    action: 'feedback_resolved',
    target_type: 'feedback',
    target_id: 'fb-4',
    details: { resolution: 'CORS configuration updated on edge function' },
    created_at: '2026-02-09T16:00:00Z',
  },
  {
    id: 'al-3',
    admin_email: 'admin@inverio.ai',
    action: 'note_added',
    target_type: 'feedback',
    target_id: 'fb-1',
    details: { note_preview: 'Reproduced on iPhone 15 Pro...' },
    created_at: '2026-02-10T14:45:00Z',
  },
  {
    id: 'al-4',
    admin_email: 'admin@inverio.ai',
    action: 'role_changed',
    target_type: 'user',
    target_id: 'u-8',
    details: { user_email: 'felix@zurich-angels.ch', role: 'investor' },
    created_at: '2026-02-05T10:35:00Z',
  },
  {
    id: 'al-5',
    admin_email: 'admin@inverio.ai',
    action: 'feedback_priority_changed',
    target_type: 'feedback',
    target_id: 'fb-4',
    details: { from: 'high', to: 'critical' },
    created_at: '2026-02-08T09:00:00Z',
  },
];

// ============================================================
// Admin feedback notes
// ============================================================

export const mockAdminFeedbackNotes: Record<string, AdminFeedbackNote[]> = {
  'fb-1': [
    {
      id: 'note-1',
      feedback_id: 'fb-1',
      admin_email: 'admin@inverio.ai',
      text: 'Reproduced on iPhone 15 Pro with iOS 18.2. The NaN appears when the score query returns before the profile data loads. Likely a race condition.',
      created_at: '2026-02-10T14:45:00Z',
    },
    {
      id: 'note-2',
      feedback_id: 'fb-1',
      admin_email: 'admin@inverio.ai',
      text: 'Added a loading guard in ScoreCard component. Deploying fix to TestFlight.',
      created_at: '2026-02-10T16:30:00Z',
    },
  ],
  'fb-4': [
    {
      id: 'note-3',
      feedback_id: 'fb-4',
      admin_email: 'admin@inverio.ai',
      text: 'CORS issue confirmed. The LinkedIn OAuth callback URL was not whitelisted in the edge function. Fixed and deployed.',
      created_at: '2026-02-09T15:30:00Z',
    },
  ],
};

// ============================================================
// User detail data
// ============================================================

export const mockAdminUserDetails: Record<string, AdminUserDetail> = {
  'u-1': {
    id: 'u-1', email: 'sarah@techstart.io', role: 'startup',
    onboarding_completed: true, created_at: '2026-01-15T10:00:00Z',
    last_active: '2026-02-11T08:30:00Z', login_count: 42,
    feedback_count: 2, message_count: 18, score: 74,
    data_room_files: 8, data_room_views: 15,
  },
  'u-2': {
    id: 'u-2', email: 'marcus@alpine.vc', role: 'investor',
    onboarding_completed: true, created_at: '2026-01-16T12:00:00Z',
    last_active: '2026-02-10T17:15:00Z', login_count: 35,
    feedback_count: 1, message_count: 24, score: null,
    data_room_files: 0, data_room_views: 22,
  },
  'u-3': {
    id: 'u-3', email: 'lena@greenfield.co', role: 'startup',
    onboarding_completed: true, created_at: '2026-01-20T09:00:00Z',
    last_active: '2026-02-09T11:00:00Z', login_count: 28,
    feedback_count: 1, message_count: 9, score: 61,
    data_room_files: 5, data_room_views: 8,
  },
  'u-4': {
    id: 'u-4', email: 'jonas@finova.ch', role: 'investor',
    onboarding_completed: true, created_at: '2026-01-22T14:00:00Z',
    last_active: '2026-02-08T16:45:00Z', login_count: 19,
    feedback_count: 1, message_count: 12, score: null,
    data_room_files: 0, data_room_views: 14,
  },
  'u-5': {
    id: 'u-5', email: 'anna@swissbio.ch', role: 'startup',
    onboarding_completed: true, created_at: '2026-01-25T11:00:00Z',
    last_active: '2026-02-11T07:00:00Z', login_count: 31,
    feedback_count: 1, message_count: 7, score: 82,
    data_room_files: 12, data_room_views: 19,
  },
  'u-6': {
    id: 'u-6', email: 'david@capitalbridge.com', role: 'investor',
    onboarding_completed: false, created_at: '2026-02-01T08:00:00Z',
    last_active: '2026-02-02T09:00:00Z', login_count: 3,
    feedback_count: 0, message_count: 0, score: null,
    data_room_files: 0, data_room_views: 0,
  },
  'u-7': {
    id: 'u-7', email: 'mia@cloudkitchen.io', role: 'startup',
    onboarding_completed: true, created_at: '2026-02-03T16:00:00Z',
    last_active: '2026-02-10T12:30:00Z', login_count: 14,
    feedback_count: 0, message_count: 5, score: 56,
    data_room_files: 3, data_room_views: 4,
  },
  'u-8': {
    id: 'u-8', email: 'felix@zurich-angels.ch', role: 'investor',
    onboarding_completed: true, created_at: '2026-02-05T10:30:00Z',
    last_active: '2026-02-10T18:00:00Z', login_count: 11,
    feedback_count: 0, message_count: 8, score: null,
    data_room_files: 0, data_room_views: 9,
  },
  'u-9': {
    id: 'u-9', email: 'claire@medtech-lab.com', role: 'startup',
    onboarding_completed: false, created_at: '2026-02-08T13:00:00Z',
    last_active: '2026-02-08T14:00:00Z', login_count: 2,
    feedback_count: 0, message_count: 0, score: null,
    data_room_files: 0, data_room_views: 0,
  },
  'u-10': {
    id: 'u-10', email: 'admin@inverio.ai', role: 'admin',
    onboarding_completed: true, created_at: '2026-01-10T08:00:00Z',
    last_active: '2026-02-11T09:00:00Z', login_count: 88,
    feedback_count: 0, message_count: 3, score: null,
    data_room_files: 0, data_room_views: 0,
  },
};

// ============================================================
// User activity summaries
// ============================================================

function generateActivityByDay(baseCount: number): { date: string; count: number }[] {
  const days: { date: string; count: number }[] = [];
  const now = new Date('2026-02-11T12:00:00Z');
  for (let i = 13; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const dateStr = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
    days.push({ date: dateStr, count: Math.max(0, baseCount + Math.floor(Math.random() * 4) - 1) });
  }
  return days;
}

export const mockUserActivity: Record<string, UserActivitySummary> = {
  'u-1': {
    activityByDay: generateActivityByDay(3),
    recentEvents: [
      { id: 'e-1', event_type: 'login', description: 'Logged in from iOS', metadata: {}, timestamp: '2026-02-11T08:30:00Z' },
      { id: 'e-2', event_type: 'score_update', description: 'Inverio Score updated to 74', metadata: { score: 74 }, timestamp: '2026-02-10T14:00:00Z' },
      { id: 'e-3', event_type: 'feedback', description: 'Submitted bug report about NaN score', metadata: { feedbackId: 'fb-1' }, timestamp: '2026-02-10T14:23:00Z' },
      { id: 'e-4', event_type: 'message', description: 'Sent message to Marcus from Alpine VC', metadata: {}, timestamp: '2026-02-09T11:00:00Z' },
      { id: 'e-5', event_type: 'upload', description: 'Uploaded pitch deck v2.pdf', metadata: { fileName: 'pitch deck v2.pdf' }, timestamp: '2026-02-08T09:30:00Z' },
    ],
  },
  'u-2': {
    activityByDay: generateActivityByDay(2),
    recentEvents: [
      { id: 'e-6', event_type: 'login', description: 'Logged in from iOS', metadata: {}, timestamp: '2026-02-10T17:15:00Z' },
      { id: 'e-7', event_type: 'data_room_view', description: 'Viewed TechStart data room', metadata: { startupId: 'u-1' }, timestamp: '2026-02-10T17:20:00Z' },
      { id: 'e-8', event_type: 'message', description: 'Sent message to Sarah from TechStart', metadata: {}, timestamp: '2026-02-10T17:30:00Z' },
      { id: 'e-9', event_type: 'feedback', description: 'Submitted feature request for CSV export', metadata: { feedbackId: 'fb-2' }, timestamp: '2026-02-09T09:45:00Z' },
    ],
  },
  'u-3': {
    activityByDay: generateActivityByDay(2),
    recentEvents: [
      { id: 'e-10', event_type: 'login', description: 'Logged in from Android', metadata: {}, timestamp: '2026-02-09T11:00:00Z' },
      { id: 'e-11', event_type: 'feedback', description: 'Submitted UX feedback about onboarding', metadata: { feedbackId: 'fb-3' }, timestamp: '2026-02-08T16:12:00Z' },
    ],
  },
};

// ============================================================
// Admin notifications (for bell icon dropdown)
// ============================================================

export interface AdminNotification {
  id: string;
  type: 'new_feedback' | 'new_signup' | 'onboarding_complete' | 'critical_bug';
  title: string;
  body: string;
  createdAt: string;
  read: boolean;
  data: Record<string, string>;
}

export const mockAdminNotifications: AdminNotification[] = [
  {
    id: 'an-1',
    type: 'new_feedback',
    title: 'New bug report',
    body: 'sarah@techstart.io reported NaN score issue',
    createdAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    read: false,
    data: { feedbackId: 'fb-1' },
  },
  {
    id: 'an-2',
    type: 'new_signup',
    title: 'New user signed up',
    body: 'claire@medtech-lab.com joined as a startup',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
    read: false,
    data: { userId: 'u-9' },
  },
  {
    id: 'an-3',
    type: 'onboarding_complete',
    title: 'Onboarding completed',
    body: 'felix@zurich-angels.ch completed investor onboarding',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    read: true,
    data: { userId: 'u-8' },
  },
  {
    id: 'an-4',
    type: 'critical_bug',
    title: 'Critical bug resolved',
    body: 'LinkedIn CORS issue has been fixed and deployed',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    read: true,
    data: { feedbackId: 'fb-4' },
  },
];
